import { fromBase64url, toBase64url } from "./base64url";

/**
 * Every request to the share server, except for its health, is signed with the key of the device making it. These
 * headers carry the device's key, when it signed, and the signature.
 */
export const SIGNATURE_HEADERS = {
    key: "bandstand-key",
    time: "bandstand-time",
    signature: "bandstand-signature",
} as const;

/** How far a request's time may be from the share server's clock, in milliseconds. Older requests are refused. */
export const MAX_CLOCK_SKEW = 5 * 60 * 1000;

/** A device's key pair. The public half, as base64url, is how the share server knows the device. */
export type DeviceKeys = {
    privateKey: CryptoKey;
    publicKey: string;
};

/** Makes a new key pair for a device. It never leaves the device; only the public half is sent. */
export async function makeDeviceKeys(): Promise<DeviceKeys> {
    const pair = (await crypto.subtle.generateKey({ name: "Ed25519" }, true, ["sign", "verify"])) as CryptoKeyPair;
    const publicKey = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));

    return { privateKey: pair.privateKey, publicKey: toBase64url(publicKey) };
}

/** The headers that sign a request: `path` with its query, and the exact body that's sent. */
export async function signRequest(keys: DeviceKeys, method: string, path: string, body: string, now = Date.now()) {
    const time = String(now);
    const text = await signedText(method, path, time, body);
    const signature = new Uint8Array(await crypto.subtle.sign({ name: "Ed25519" }, keys.privateKey, text));

    return {
        [SIGNATURE_HEADERS.key]: keys.publicKey,
        [SIGNATURE_HEADERS.time]: time,
        [SIGNATURE_HEADERS.signature]: toBase64url(signature),
    };
}

/** Checks a request's signature and time, and says which device's key signed it, or why it can't be trusted. */
export async function verifyRequest(
    headers: (name: string) => string | undefined,
    method: string,
    path: string,
    body: string,
    now = Date.now(),
): Promise<{ key: string } | { error: string }> {
    const [key, time, signature] = Object.values(SIGNATURE_HEADERS).map(headers);

    if (!key || !time || !signature) return { error: "This request isn't signed." };
    if (!(Math.abs(now - Number(time)) <= MAX_CLOCK_SKEW)) return { error: "This request's time is too far from the server's clock." };

    try {
        const publicKey = await crypto.subtle.importKey("raw", fromBase64url(key), { name: "Ed25519" }, false, ["verify"]);
        const text = await signedText(method, path, time, body);

        if (await crypto.subtle.verify({ name: "Ed25519" }, publicKey, fromBase64url(signature), text)) return { key };
    } catch {
        // A key or signature that isn't base64url, or isn't the right length, can't check out either.
    }

    return { error: "This request's signature doesn't check out." };
}

/** What a signature covers: the method, path and query, time, and a hash of the body, one per line. */
async function signedText(method: string, path: string, time: string, body: string): Promise<Uint8Array<ArrayBuffer>> {
    const bodyHash = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(body)));

    return new TextEncoder().encode([method.toUpperCase(), path, time, toBase64url(bodyHash)].join("\n"));
}
